import { Ref, forwardRef } from "react";

import { childrenToString } from "../../internal/functions";
import { DataTrackingId, DistributiveOmit } from "../../types";
import { useTrackingId } from "../../hooks";

import { LinkProps as BaseLinkProps, Link as BaseLink } from "./internal/Link";

/**
 * Props for the Link component.
 * @extends Omit<BaseLinkProps, "href">
 * @extends DataTrackingId
 */
export type LinkProps = DistributiveOmit<BaseLinkProps, "href"> &
  DataTrackingId &
  (
    | {
        pathname?: never;
        search?: never;
        href?: BaseLinkProps["href"];
      }
    | {
        pathname: string;
        search?: string;
        href?: never;
      }
    | {
        pathname?: never;
        search?: never;
        href?: never;
      }
  );

/**
 * Link component for creating accessible, styled hyperlinks with tracking support.
 *
 * Features:
 * - Two visual appearances: primary (default) and secondary
 * - Quiet mode for less prominent styling
 * - Automatic external link icon for target="_blank" links
 * - Security attributes (rel="noopener noreferrer") for external links
 * - Full accessibility support with proper focus management
 * - Supports layout utilities for positioning and spacing
 * - Flexible content support for text and other elements
 * - Responsive design with consistent typography
 * - Hover and focus states with proper visual feedback
 * - Screen reader support for external link announcements
 * - Automatic tracking ID generation for analytics
 * - Client-side navigation support with pathname and search params
 * - History API integration for single-page application navigation
 *
 * @example
 * <Link href="/dashboard" appearance="primary">
 *   Go to Dashboard
 * </Link>
 *
 * @example
 * <Link pathname="/settings" search="?tab=profile">
 *   Profile Settings
 * </Link>
 *
 * @example
 * <Link href="https://example.com" target="_blank" ghost>
 *   External Documentation
 * </Link>
 *
 * @example
 * <Link href="/profile" gap="2" alignItems="center">
 *   <Avatar name="John Doe" size="small" />
 *   View Profile
 * </Link>
 */
export const Link = forwardRef(
  (props: LinkProps, ref: Ref<HTMLAnchorElement>) => {
    const { onClick, pathname, children, search, ...rest } = props;
    const data = {
      appearance: props.appearance,
      ghost: props.ghost,
      children: childrenToString(children),
    };

    const handleClick = (
      ...args: Parameters<NonNullable<LinkProps["onClick"]>>
    ) => {
      if (pathname) {
        const to = pathname + (search ?? "");
        args[0].preventDefault();
        history.pushState(null, "", to);
        onClick?.(...args);
      } else {
        onClick?.(...args);
      }
    };

    const trackingId = useTrackingId({
      name: "Link",
      data,
      hasOverride: !!rest["data-tracking-id"],
    });

    return (
      <BaseLink
        onClick={handleClick}
        ref={ref}
        data-tracking-id={trackingId}
        {...rest}
      >
        {children}
      </BaseLink>
    );
  },
);

Link.displayName = BaseLink.displayName;
